import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { movementBadgeClass, movementLabel, accentIconClass } from "../lib/display";
import useAutoRefresh from "../lib/useAutoRefresh";
import {
  Boxes,
  Building2,
  Grid3x3,
  Package,
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  TrendingUp,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MyTasksWidget } from "../components/tasks/MyTasksWidget";

function StatCard({ icon: Icon, label, value, accent = "primary", testid, to }) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="ticker text-[color:var(--text-3)]">{label}</div>
        <div
          className={`w-8 h-8 hard flex items-center justify-center ${accentIconClass(accent)}`}
        >
          <Icon size={16} strokeWidth={2.5} />
        </div>
      </div>
      <div className="mt-4 text-4xl font-black tracking-tighter">{value}</div>
      {to && (
        <div className="mt-2 ticker text-[10px] text-[color:var(--primary)] opacity-0 group-hover:opacity-100 transition-opacity">
          Detay →
        </div>
      )}
    </>
  );
  const baseClass =
    "hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 group";
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`${baseClass} hover:border-[color:var(--primary)] transition-colors cursor-pointer block`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className={baseClass}>
      {inner}
    </div>
  );
}

/**
 * Dashboard section listing every empty sehpa side-slot (e.g. "A-7", "F-12").
 * Empty slots are prime candidates for the next incoming stock and letting
 * the warehouse team see them at a glance saves running to the depot.
 * A side-group filter (A, B, C…) lets the user zoom into the specific side
 * of the warehouse they are working at.
 */
function EmptySlotsPanel({ positions }) {
  const [expanded, setExpanded] = React.useState(false);
  const [group, setGroup] = React.useState("");
  const groups = React.useMemo(() => {
    const seen = new Map();
    for (const p of positions) {
      const prefix = String(p).split("-")[0] || "";
      if (!prefix) continue;
      seen.set(prefix, (seen.get(prefix) || 0) + 1);
    }
    return Array.from(seen.entries())
      .map(([prefix, count]) => ({ prefix, count }))
      .sort((a, b) => a.prefix.localeCompare(b.prefix, undefined, { numeric: true }));
  }, [positions]);
  const filtered = React.useMemo(
    () => (group ? positions.filter((p) => String(p).split("-")[0] === group) : positions),
    [positions, group],
  );
  const preview = expanded ? filtered : filtered.slice(0, 24);
  // Collapse the "show more" when switching group so the user sees the
  // first page of the newly filtered list.
  React.useEffect(() => {
    setExpanded(false);
  }, [group]);
  return (
    <div
      data-testid="dashboard-empty-slots-panel"
      className="hard border border-[color:var(--border)] bg-[color:var(--surface)]"
    >
      <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Grid3x3 size={18} className="text-[color:var(--success)]" />
          <h3 className="font-bold">Boş Sehpalar</h3>
        </div>
        <div className="ticker text-[color:var(--text-3)]">
          {group
            ? `${filtered.length} boş yan · ${group} grubu`
            : `${positions.length} boş yan · yeni stok için hazır`}
        </div>
      </div>
      {groups.length > 1 && (
        <div
          data-testid="dashboard-empty-slots-groups"
          className="px-3 pt-3 pb-1 flex flex-wrap gap-1.5 border-b border-[color:var(--border)]"
        >
          <button
            data-testid="dashboard-empty-slots-group-all"
            onClick={() => setGroup("")}
            className={`px-2.5 py-1 hard text-[11px] font-mono font-bold uppercase tracking-wider transition-colors ${
              group === ""
                ? "bg-[color:var(--primary)] text-white border-2 border-[color:var(--primary)]"
                : "border-2 border-[color:var(--border)] bg-white hover:border-[color:var(--primary)]"
            }`}
          >
            Tümü · {positions.length}
          </button>
          {groups.map((g) => (
            <button
              key={g.prefix}
              data-testid={`dashboard-empty-slots-group-${g.prefix}`}
              onClick={() => setGroup(g.prefix)}
              className={`px-2.5 py-1 hard text-[11px] font-mono font-bold uppercase tracking-wider transition-colors ${
                group === g.prefix
                  ? "bg-[color:var(--primary)] text-white border-2 border-[color:var(--primary)]"
                  : "border-2 border-[color:var(--border)] bg-white hover:border-[color:var(--primary)]"
              }`}
            >
              {g.prefix} · {g.count}
            </button>
          ))}
        </div>
      )}
      <div className="p-3 flex flex-wrap gap-1.5">
        {filtered.length === 0 && (
          <div className="w-full py-8 text-center text-xs text-[color:var(--text-3)]">
            Bu grupta boş sehpa yok.
          </div>
        )}
        {preview.map((p) => (
          <Link
            key={p}
            to={`/stok?sehpa=${encodeURIComponent(p)}`}
            data-testid={`dashboard-empty-slot-${p}`}
            title={`${p} boş — tıklayınca stoğa filtreli aç`}
            className="inline-flex items-center px-2 py-1 hard border-2 border-dashed border-[color:var(--border)] bg-[color:var(--success-bg)] hover:border-[color:var(--success)] hover:text-[color:var(--success)] text-xs font-mono font-bold transition-colors"
          >
            {p}
          </Link>
        ))}
        {!expanded && filtered.length > 24 && (
          <button
            onClick={() => setExpanded(true)}
            data-testid="dashboard-empty-slots-more"
            className="px-3 py-1 text-xs underline text-[color:var(--text-2)] hover:text-[color:var(--primary)]"
          >
            +{filtered.length - 24} tümünü göster
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Panel showing shelves that are physically occupied by non-glass items
 * (e.g. machine parts on AX-2 … AX-11). These are excluded from "Boş Sehpalar"
 * so operators aren't misled into staging new stock on top of the machinery.
 */
function ReservedSlotsPanel({ positions, label }) {
  return (
    <div
      data-testid="dashboard-reserved-slots-panel"
      className="hard border border-[color:var(--border)] bg-[color:var(--surface)]"
    >
      <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench size={18} className="text-[color:var(--warning)]" />
          <h3 className="font-bold">Rezerve · {label}</h3>
        </div>
        <div className="ticker text-[color:var(--text-3)]">
          {positions.length} sehpa cam dışı ürün · boş değil
        </div>
      </div>
      <div className="p-3 flex flex-wrap gap-1.5">
        {positions.map((p) => (
          <Link
            key={p}
            to="/makina-parcalari"
            data-testid={`dashboard-reserved-slot-${p}`}
            title={`${p} — ${label} (tıkla → detay)`}
            className="inline-flex items-center px-2 py-1 hard border-2 border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] text-xs font-mono font-bold hover:brightness-110 transition"
          >
            {p}
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Payment-alert banner shown on Dashboard when there are unpaid GES invoices
 * that are either overdue or coming due within 7 days.
 */
export default function Dashboard() {
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState(null);
  const [error, setError] = useState("");

  const fetchStats = React.useCallback(() => {
    api
      .get("/stats")
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e)));
    api
      .get("/stats/brands")
      .then(({ data }) => setBrands(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useAutoRefresh(fetchStats);

  if (error)
    return (
      <div className="hard border border-[color:var(--error)] bg-[color:var(--error-bg)] text-[color:var(--error)] p-4">
        {error}
      </div>
    );

  if (!data)
    return <div className="ticker text-[color:var(--text-3)]">Yükleniyor…</div>;

  return (
    <div className="space-y-6">
      <MyTasksWidget />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Boxes}
          label="Toplam Ürün"
          value={data.total_products.toLocaleString("tr-TR")}
          testid="dashboard-total-stock"
          to="/stok?include_empty=0"
        />
        <StatCard
          icon={Package}
          label="Toplam Adet"
          value={data.total_adet.toLocaleString("tr-TR")}
          accent="accent"
          testid="dashboard-total-adet"
          to="/stok"
        />
        <StatCard
          icon={Grid3x3}
          label="Sehpa Sayısı"
          value={data.shelves_count}
          testid="dashboard-shelves"
          to="/sehpalar"
        />
        <StatCard
          icon={AlertTriangle}
          label="Düşük Stok (≤2)"
          value={data.low_stock}
          accent="warning"
          testid="dashboard-low-stock"
          to="/stok?adet=low"
        />
        <StatCard
          icon={Grid3x3}
          label="Boş Sehpa"
          value={`${data.empty_slots_count ?? 0} / ${data.total_slots ?? 216}`}
          accent="success"
          testid="dashboard-empty-slots"
          to="/depo-3d"
        />
      </div>

      {data.empty_slots && data.empty_slots.length > 0 && (
        <EmptySlotsPanel positions={data.empty_slots} />
      )}

      {data.reserved_slots && data.reserved_slots.length > 0 && (
        <ReservedSlotsPanel
          positions={data.reserved_slots}
          label={data.reserved_label || "Rezerve"}
        />
      )}

      {/* Firma bazlı stok */}
      {brands && (
        <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
          <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-[color:var(--primary)]" />
              <h3 className="font-bold">Firma Bazlı Stok</h3>
            </div>
            <div className="ticker text-[color:var(--text-3)]">
              {brands.brands.length} marka + bilinmeyen
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4">
            {brands.brands.map((b) => (
              <Link
                key={b.firma}
                to={`/stok?firma=${encodeURIComponent(b.firma)}`}
                data-testid={`dashboard-brand-card-${b.keyword.toLowerCase().replace(/\s+/g, "-")}`}
                className="hard border border-[color:var(--border)] bg-white p-3 hover:border-[color:var(--primary)] transition-colors group"
              >
                <div className="ticker text-[10px] text-[color:var(--text-3)] mb-2 truncate">
                  {b.firma}
                </div>
                <div className="text-3xl font-black tracking-tighter">
                  {b.total_adet.toLocaleString("tr-TR")}
                </div>
                <div className="text-xs text-[color:var(--text-2)] mt-1">
                  <span className="font-mono font-bold">{b.product_count}</span>
                  <span className="ticker ml-1 text-[color:var(--text-3)]">
                    ürün
                  </span>
                </div>
              </Link>
            ))}
            <Link
              to="/stok?firma=BILINMEYEN"
              data-testid="dashboard-brand-card-unknown"
              className="hard border border-dashed border-[color:var(--warning)] bg-[color:var(--warning-bg)] p-3 hover:border-[color:var(--warning)] hover:border-solid transition-colors group"
              title="Firması belirlenemeyen ürünler — tıklayıp firma atayın"
            >
              <div className="ticker text-[10px] text-[color:var(--warning)] mb-2 flex items-center gap-1">
                BİLİNMEYEN
                <span className="text-[9px] text-[color:var(--text-3)]">→ ata</span>
              </div>
              <div className="text-3xl font-black tracking-tighter text-[color:var(--warning)]">
                {brands.unknown.total_adet.toLocaleString("tr-TR")}
              </div>
              <div className="text-xs text-[color:var(--text-2)] mt-1">
                <span className="font-mono font-bold">
                  {brands.unknown.product_count}
                </span>
                <span className="ticker ml-1 text-[color:var(--text-3)]">
                  ürün
                </span>
              </div>
            </Link>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5">
          <div className="ticker text-[color:var(--text-3)]">Bugün</div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 hard bg-[color:var(--success-bg)] text-[color:var(--success)] flex items-center justify-center">
                <ArrowDownToLine size={18} strokeWidth={2.5} />
              </div>
              <div>
                <div className="ticker text-[10px]">Giriş</div>
                <div
                  data-testid="dashboard-entries-today"
                  className="text-2xl font-black"
                >
                  {data.entries_today}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 hard bg-[color:var(--error-bg)] text-[color:var(--error)] flex items-center justify-center">
                <ArrowUpFromLine size={18} strokeWidth={2.5} />
              </div>
              <div>
                <div className="ticker text-[10px]">Çıkış</div>
                <div
                  data-testid="dashboard-exits-today"
                  className="text-2xl font-black"
                >
                  {data.exits_today}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/toplu-giris"
          data-testid="quick-action-entry"
          className="hard border border-[color:var(--border)] bg-[color:var(--primary)] text-white p-5 flex items-center justify-between hover:bg-[color:var(--primary-hover)] transition-colors"
        >
          <div>
            <div className="ticker text-white/60">Hızlı İşlem</div>
            <div className="text-2xl font-black mt-2">Toplu Giriş</div>
            <div className="text-xs text-white/70 mt-1">
              Barkod okut, camı sisteme ekle
            </div>
          </div>
          <ArrowDownToLine size={40} strokeWidth={2.5} />
        </Link>

        <Link
          to="/toplu-cikis"
          data-testid="quick-action-exit"
          className="hard border border-[color:var(--border)] bg-[color:var(--accent)] text-white p-5 flex items-center justify-between hover:bg-[color:var(--accent-hover)] transition-colors"
        >
          <div>
            <div className="ticker text-white/70">Hızlı İşlem</div>
            <div className="text-2xl font-black mt-2">Toplu Çıkış</div>
            <div className="text-xs text-white/80 mt-1">
              Müşteri fişi oluştur, çıkış yap
            </div>
          </div>
          <ArrowUpFromLine size={40} strokeWidth={2.5} />
        </Link>
      </div>

      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} />
            <h3 className="font-bold">Son Hareketler</h3>
          </div>
          <Link
            to="/hareketler"
            data-testid="dashboard-view-all-movements"
            className="ticker text-[color:var(--primary)] hover:underline"
          >
            Tümü →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table w-full">
            <thead>
              <tr>
                <th>Tip</th>
                <th>Barkod</th>
                <th>Ürün</th>
                <th>Adet</th>
                <th>Sehpa</th>
                <th>Kullanıcı</th>
                <th>Tarih</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-[color:var(--text-3)] py-8">
                    Henüz hareket yok.
                  </td>
                </tr>
              )}
              {data.recent.map((m) => (
                <tr key={m.id} data-testid="dashboard-recent-row">
                  <td>
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase hard ${movementBadgeClass(m.type)}`}
                    >
                      {movementLabel(m.type)}
                    </span>
                  </td>
                  <td className="font-mono">{m.barkod}</td>
                  <td className="max-w-xs truncate">{m.isim}</td>
                  <td className="font-mono font-bold">{m.adet}</td>
                  <td className="font-mono">{m.sehpa_konumu}</td>
                  <td>{m.user_name}</td>
                  <td className="text-[color:var(--text-2)]">
                    {m.created_at ? new Date(m.created_at).toLocaleString("tr-TR") : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
